// One id per run of a credit-billed workflow ('import_workflow', 'export_workflow',
// 'design_assistant'). Sent with every step spend so the server bills each (run, step)
// once and the ledger can be audited per run.
//
// Kept in sessionStorage so it survives the workflow unmounting mid-run (e.g. Export
// hands off to the Classify tool for an HTS code and returns), and wiped when the user
// starts over ("New Design", "New Import", "Start over"), which begins a new run.
type RunWorkflow = 'import_workflow' | 'export_workflow' | 'design_assistant';

const storageKey = (workflow: RunWorkflow, teamId: string) =>
  `bite:workflow-run:${workflow}:${teamId}`;

const newRunId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const memory = new Map<string, string>();

export const getWorkflowRunId = (workflow: RunWorkflow, teamId: string): string => {
  const key = storageKey(workflow, teamId);
  try {
    const stored = window.sessionStorage.getItem(key);
    if (stored) return stored;
    const created = newRunId();
    window.sessionStorage.setItem(key, created);
    return created;
  } catch {
    // Storage blocked: fall back to memory so the run still has one stable id per page load.
    if (!memory.has(key)) memory.set(key, newRunId());
    return memory.get(key) as string;
  }
};

export const clearWorkflowRunId = (workflow: RunWorkflow, teamId: string | undefined): void => {
  if (!teamId) return;
  const key = storageKey(workflow, teamId);
  memory.delete(key);
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // nothing to clear
  }
};
